import './theme.scss'

import {DOMAttrs} from '@codeurs/front'
import React, {FunctionComponent, HTMLProps, useState} from 'react'
import KeyboardEventHandler from 'react-keyboard-event-handler'
import {ArrowIcon, DownloadIcon} from 'styles/inline-icons'
import {Mod, style} from 'util/campagne/style'
import {Trusted} from 'util/campagne/trusted'
import {createTheme} from 'util/createtheme'
import {Link as UtilLink, LinkProps} from 'util/link'
import {Button, ButtonIcon, ButtonPrimary, ButtonText} from '../ui/Button'

const appEnv: string = (window as any).__APP_ENV__

const styles = style('theme', [
	'p',
	'h1',
	'h2',
	'h2parted',
	'h3',
	'h4',
	'h5',
	'hr',
	'quote',
	'container',
	'wysiwyg',
	'link'
])

type PModType = Mod<'green' | 'small' | 'white' | 'bold'>
type HModType = Mod<
	| 'green'
	| 'white'
	| 'marked'
	| 'slantedtoleft'
	| 'nobackground'
	| 'nowrap'
	| 'big'
	| 'shadow'
>
type H2ModType = HModType &
	Mod<'slantedtoright' | 'rastered' | 'small' | 'white'>
type P = React.FC<HTMLProps<HTMLParagraphElement> & {mod?: PModType}>

const P: P = ({mod, ...props}) => (
	<p {...props} className={styles.p.mod(mod).mergeProps(props)} />
)

type Header = React.FC<HTMLProps<HTMLHeadingElement> & {mod?: HModType}>
type HeaderH2 = React.FC<HTMLProps<HTMLHeadingElement> & {mod?: H2ModType}>

const H1: Header = ({as: Tag = 'h1', mod, className = null, ...props}) => (
	<Tag
		{...props}
		class={styles.h1
			.mod(mod)
			.with(className && className.toString())
			.mergeProps(props)}
	/>
)
const H2: HeaderH2 = ({as: Tag = 'h2', mod, className = null, ...props}) => (
	<Tag
		{...props}
		class={styles.h2
			.mod(mod)
			.with(className && className.toString())
			.mergeProps(props)}
	/>
)

const H2Parted: React.FC<
	HTMLProps<HTMLDivElement> & {firstLabel: string; secondLabel: string}
> = ({firstLabel, secondLabel, ...props}) => (
	<span {...props} className={styles.h2parted.mergeProps(props).toString()}>
		<h2
			class={styles.h2
				.mod(['small', 'marked', 'slantedtoleft', 'green'])
				.with('firstitem')}
		>
			{firstLabel}
		</h2>
		<span
			class={styles.h2
				.mod(['duplicate', 'small', 'slantedtoleft', 'marked', 'green'])
				.with('firstitem')}
		>
			{firstLabel}
		</span>

		<h2
			class={styles.h2
				.mod(['small', 'marked', 'slantedtoright'])
				.with('seconditem')}
		>
			{secondLabel}
		</h2>
		<span
			class={styles.h2
				.mod(['duplicate', 'small', 'marked', 'slantedtoright', 'grey'])
				.with('seconditem')}
		>
			{secondLabel}
		</span>
	</span>
)

const H3: Header = ({as: Tag = 'h3', mod, ...props}) => (
	<Tag {...props} class={styles.h3.mod(mod).mergeProps(props)} />
)
const H4: Header = ({as: Tag = 'h4', mod, ...props}) => (
	<Tag {...props} class={styles.h4.mod(mod).mergeProps(props)} />
)
const H5: Header = ({as: Tag = 'h5', mod, ...props}) => (
	<Tag {...props} class={styles.h5.mod(mod).mergeProps(props)} />
)
const Quote: FunctionComponent<HTMLProps<HTMLQuoteElement>> = ({...props}) => (
	<div class={styles.quote.mergeProps(props)}>
		<q class={styles.quote('quote')}>{props.children}</q>
	</div>
)

const HR: FunctionComponent<HTMLProps<HTMLHRElement>> = () => (
	<hr class={styles.hr} />
)

type Div = React.FC<HTMLProps<HTMLDivElement>>
type ContainerType = React.FC<
	HTMLProps<HTMLDivElement> & {mod?: 'shifted' | 'shifttoright'}
>

const Container: ContainerType = ({children, mod, ...props}) => (
	<div {...props} className={styles.container.mod(mod).toString()}>
		{children}
		<DevGrid />
	</div>
)

const DevGrid: Div = ({children, ...props}) => {
	const appUrl = window.location.href
	const isDev = appUrl && appUrl.indexOf('codeu.rs') !== -1
	if (!isDev) return null

	const [isShown, setShown] = useState(
		JSON.parse(localStorage.getItem('showGrid'))
	)
	return (
		<>
			{isShown && (
				<div class={styles.container('dev')}>
					{[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1].map(_ => (
						<div class={styles.container('dev-item')}></div>
					))}
				</div>
			)}
			<KeyboardEventHandler
				handleKeys={['h']}
				onKeyEvent={(key, e) => {
					localStorage.setItem('showGrid', JSON.stringify(!isShown))
					setShown(!isShown)
				}}
			/>
		</>
	)
}

const Wysiwyg: React.FC<
	{
		mod?: Mod<'white'>
		children: string
	} & DOMAttrs
> = ({mod, ...props}) => (
	<Trusted
		{...props}
		className={styles.wysiwyg
			.mod(mod)
			.mergeProps(props)
			.toString()}
	/>
)

const Link: React.FC<
	LinkProps & {
		withArrow?: boolean
		arrowLeft?: boolean
		className?: string
	}
> = ({children, withArrow, arrowLeft, className, ...props}) => (
	<div class={styles.link.with(className).mergeProps(props)}>
		<UtilLink {...props}>{children}</UtilLink>
		{withArrow && (
			<ArrowIcon
				className={styles
					.link('svg')
					.mod(arrowLeft && 'arrowleft')
					.toString()}
			/>
		)}
	</div>
)

const LinkDownload: React.FC<{mod?: 'small'} & LinkProps> = ({
	mod,
	children,
	id,
	...props
}) => (
	<div
		class={styles.link
			.mod('download')
			.mod(mod)
			.mergeProps(props)}
	>
		<UtilLink {...props}>
			<span id={`${id}-text`}>{children}</span>
			<DownloadIcon id={`${id}-icon`} />
		</UtilLink>
	</div>
)

export const defaultTheme = {
	P,
	H1,
	H2,
	H2Parted,
	H3,
	H4,
	H5,
	Quote,
	HR,
	Container,
	Wysiwyg,
	Button,
	ButtonPrimary,
	ButtonText,
	ButtonIcon,
	LinkDownload,
	Link
}

export const Theme = createTheme(defaultTheme)
